import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import ContactModal from "../components/ContactModal";
import { useLanguage } from "../contexts/LanguageContext";
import { 
  Building2, 
  TrendingUp,
  Target,
  ArrowRight,
  Shield,
  CheckCircle,
  BarChart3,
  FileCheck,
  Briefcase,
  Scale,
  Lock,
  Search,
  Rocket,
  Award,
  Calendar,
  Clock,
  Database,
  LineChart,
  Zap
} from "lucide-react";

const PrivateEquity = () => {
  const { currentLanguage } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const seoData = {
    title: 'KI-Value-Engine für Private Equity | Maximale Wertsteigerung - BROVKO',
    description: 'KI-Strategien für Private Equity: Von Due Diligence bis Exit-Vorbereitung. Maximale Wertsteigerung im Portfolio durch systematische KI-Transformation mit messbarem EBIT-Impact.',
    canonicalUrl: '/private-equity',
    keywords: 'Private Equity KI, PE Value Creation, Portfolio Wertsteigerung, KI Due Diligence, Exit Vorbereitung, Multiple Expansion, AIWizz, ISO 42001, Operating Partners, General Partners, EBIT Impact',
    ogTitle: 'KI-Value-Engine für Private Equity - Maximale Wertsteigerung',
    ogDescription: 'Von Due Diligence bis Exit: Systematische KI-Transformation für PE-Portfolios mit messbarem EBIT-Impact und ISO 42001 Compliance.',
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Lösungen', url: '/roi-business-impact' },
      { name: 'Private Equity', url: '/private-equity' }
    ],
    faqData: [
      {
        question: 'Wie hilft KI bei Private Equity Investitionen?',
        answer: 'KI unterstützt den gesamten PE-Investitionszyklus: In der Due Diligence identifiziert sie versteckte Effizienzpotenziale, während der Holding Period implementiert die KI-Value-Engine skalierbare Margenverbesserungen, und bei der Exit-Vorbereitung steigert nachweisbare KI-Souveränität (ISO 42001) den Multiple.'
      },
      {
        question: 'Was ist Multiple Expansion durch KI-Souveränität?',
        answer: 'Ein KI-Stack auf Basis von AIWizz steigert den Unternehmenswert durch: 1) Auditability - jeder KI-Output ist durch Knowledge Graphs belegbar, 2) Determinismus - verlässliche Daten ohne Halluzinationen, 3) IP-Schutz - 100% Datenhoheit ohne US-Cloud-Abhängigkeit.'
      },
      {
        question: 'Wie schnell sehen PE-Portfolios ROI?',
        answer: 'Mit der KI-Value-Engine erreichen PE-Portfolio-Companies typischerweise ROI in unter 12 Monaten. Die 12-Monats-Roadmap liefert klare Meilensteine, messbare KPIs und regelmäßiges Board-Reporting für Investment-Komitees.'
      }
    ],
    serviceData: {
      name: 'KI-Value-Engine für Private Equity',
      description: 'Systematische KI-Transformation für PE-Portfolios: Due Diligence, Value Creation, Exit-Vorbereitung',
      serviceType: 'Private Equity AI Consulting'
    }
  };

  // PE Investment Lifecycle Phases
  const investmentPhases = [
    {
      phase: 'Phase 1',
      title: 'Due Diligence',
      subtitle: 'Pre-Inking',
      icon: Search,
      color: 'from-blue-500 to-blue-600',
      description: 'KI-Potenzialanalyse und Risiko-Assessment. Identifikation von versteckten Effizienzhebeln vor dem Kauf.',
      deliverables: [
        'KI-Readiness Score des Targets',
        'Operative Automatisierungspotenziale',
        'Tech-Stack Risk Assessment',
        'Value Creation Roadmap'
      ]
    },
    {
      phase: 'Phase 2',
      title: 'Holding Period',
      subtitle: 'Value Creation',
      icon: Rocket,
      color: 'from-brand to-brand-600',
      description: 'Implementierung der KI-Value-Engine zur Skalierung von Margen. Aufbau von Enterprise-KI-Strukturen auf Basis von AIWizz.',
      deliverables: [
        'KI-Value-Engine Implementierung',
        'Operational Excellence Programme',
        'Cross-Portfolio Synergien',
        'EBIT-Impact Tracking'
      ]
    },
    {
      phase: 'Phase 3',
      title: 'Exit-Preparation',
      subtitle: 'Multiple Maximierung',
      icon: TrendingUp,
      color: 'from-emerald-500 to-emerald-600',
      description: 'Maximierung des Multiples durch nachweisbare KI-Souveränität und skalierbare Tech-Stacks. ISO 42001 Ready für einen reibungslosen Verkauf.',
      deliverables: [
        'ISO 42001 Compliance Nachweis',
        'Dokumentierte KI-Governance',
        'Skalierbare Tech-Infrastruktur',
        'Exit-Story mit KI-Narrativ'
      ]
    }
  ];

  // Multiple Expansion Arguments
  const multipleExpansionArgs = [
    {
      icon: FileCheck,
      title: 'Auditability',
      description: 'Jeder KI-Output ist durch Knowledge Graphs belegbar – ein massiver Pluspunkt beim Käufer-Audit.',
      highlight: 'Transparente Entscheidungspfade'
    },
    {
      icon: Database,
      title: 'Determinismus',
      description: 'Verlässliche Finanz- und Prozessdaten ohne Halluzinationen. Reproduzierbare Ergebnisse für Ihre Due Diligence.',
      highlight: 'Keine Blackbox-Risiken'
    },
    {
      icon: Lock,
      title: 'IP-Schutz',
      description: 'Das geistige Eigentum der Portfoliogesellschaft bleibt zu 100% geschützt und autark. Keine Abhängigkeit von US-Hyperscalern.',
      highlight: '100% Datenhoheit'
    }
  ];

  // Track Record Metrics
  const trackRecord = [
    {
      metric: '< 12',
      unit: 'Monate',
      label: 'ROI über das gesamte Portfolio-Segment',
      icon: Clock
    },
    {
      metric: '+15-25%',
      unit: '',
      label: 'Multiple-Steigerung durch KI-gestützte Prozessautomatisierung',
      icon: TrendingUp
    },
    {
      metric: '100%',
      unit: '',
      label: 'Compliance mit EU AI Act durch standardisierte Governance',
      icon: Shield
    }
  ];

  return (
    <div className="theme-brand bg-surface text-ink min-h-screen bg-surface">
      <SEOHead {...seoData} />
      <Header />

      {/* Hero Section - High-End Consulting Look */}
      <section className="relative pt-40 pb-12 overflow-hidden">
        {/* Subtle Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-10 w-96 h-96 bg-brand rounded-full mix-blend-multiply filter blur-3xl"></div>
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-[46rem]">
            <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight transition-all duration-700 delay-100 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
              KI-Value-Engine für Private Equity:{' '}
              <span className="bg-brand-accent bg-clip-text text-transparent">
                Maximale Wertsteigerung im Portfolio.
              </span>
            </h1>

            <p className={`text-lg text-ink-muted mb-4 max-w-[62ch] transition-all duration-700 delay-200 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
              Vom Due Diligence Check bis zur Exit-Vorbereitung. Wir transformieren Portfoliogesellschaften in KI-gestützte Asset-Werte mit messbarem EBIT-Impact.
            </p>

            <p className={`text-base text-ink-faint mb-8 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
              Ergebnisse in Monaten, nicht Jahren. Diskret. Schnell. Messbar.
            </p>

            <div className={`flex flex-col sm:flex-row gap-3 justify-center transition-all duration-700 delay-400 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
              <Button 
                onClick={() => setIsContactModalOpen(true)}
                className="bg-brand hover:bg-brand-600 text-ink-onBrand px-6 py-3 font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all group"
              >
                <Briefcase className="mr-2 h-4 w-4" />
                PE-Case Study anfordern
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Link to="/ki-value-check">
                <Button variant="outline" className="border-hairline-strong text-white hover:bg-surface-raised px-6 py-3">
                  KI-Value-Check starten
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Lifecycle Section */}
      <section className="py-20 md:py-28 bg-slate-800/30">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-10">
            <span className="inline-block bg-blue-500/20 text-blue-300 px-3 py-1.5 rounded-full text-xs font-medium mb-3">
              Investment Lifecycle
            </span>
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3">
              Der PE-Investitionszyklus mit KI-Value-Engine
            </h2>
            <p className="text-base text-ink-faint max-w-[62ch]">
              Systematische Wertsteigerung in jeder Phase des Investment-Zyklus.
            </p>
          </div>

          {/* Timeline */}
          <div className="max-w-5xl mx-auto">
            {/* Connection Line */}
            <div className="hidden lg:block relative">
              <div className="absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full"></div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              {investmentPhases.map((phase, index) => (
                <div key={index} className="relative">
                  {/* Phase Circle */}
                  <div className="flex justify-center mb-4 lg:mb-6">
                    <div className={`w-14 h-14 bg-gradient-to-br ${phase.color} rounded-full flex items-center justify-center shadow-lg relative z-10`}>
                      <phase.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>

                  {/* Phase Content */}
                  <div className="bg-surface-raised/50 backdrop-blur border border-hairline rounded-xl p-5 hover:border-teal-500/30 transition-all duration-300">
                    <div className="text-center mb-4">
                      <span className="text-brand-400 text-xs font-bold uppercase tracking-wider">{phase.phase}</span>
                      <h3 className="text-lg font-bold text-white mt-1">{phase.title}</h3>
                      <p className="text-ink-faint text-xs">{phase.subtitle}</p>
                    </div>

                    <p className="text-ink-muted text-sm mb-4 text-center">
                      {phase.description}
                    </p>

                    <div className="border-t border-hairline pt-4">
                      <p className="text-ink-faint text-xs uppercase tracking-wider mb-2">Deliverables</p>
                      <ul className="space-y-1.5">
                        {phase.deliverables.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-xs">
                            <CheckCircle className="h-3 w-3 text-brand-400 mt-0.5 flex-shrink-0" />
                            <span className="text-ink-faint">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Multiple Expansion Section */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-10">
            <span className="inline-block bg-emerald-500/20 text-emerald-300 px-3 py-1.5 rounded-full text-xs font-medium mb-3">
              Multiple Expansion
            </span>
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3">
              Wertsteigerung durch <span className="text-brand-400">KI-Souveränität</span>
            </h2>
            <p className="text-base text-ink-faint max-w-[62ch]">
              Warum ein KI-Stack auf Basis von AIWizz den Unternehmenswert für Käufer signifikant steigert.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {multipleExpansionArgs.map((arg, index) => (
              <div 
                key={index}
                className="bg-surface-raised/50 backdrop-blur border border-hairline rounded-xl p-6 hover:border-teal-500/30 transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-brand/15 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <arg.icon className="h-6 w-6 text-brand-400" />
                </div>
                
                <h3 className="text-lg font-bold text-white mb-2">{arg.title}</h3>
                <p className="text-ink-faint text-sm mb-4">{arg.description}</p>
                
                <div className="bg-brand/10 border border-teal-500/30 rounded-lg px-3 py-2">
                  <span className="text-brand-400 text-xs font-semibold">{arg.highlight}</span>
                </div>
              </div>
            ))}
          </div>

          {/* ISO 42001 Highlight */}
          <div className="max-w-[62ch] mt-8">
            <div className="bg-slate-800/30 border border-hairline rounded-xl p-5 flex flex-col md:flex-row items-center gap-4">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-emerald-500/20 rounded-full flex items-center justify-center">
                  <Shield className="h-7 w-7 text-emerald-400" />
                </div>
              </div>
              <div className="text-center md:text-left">
                <h4 className="text-white font-bold mb-1">ISO 42001 Ready</h4>
                <p className="text-ink-faint text-sm">
                  Standardisierte KI-Governance für reibungslose Due Diligence. Der Käufer bekommt ein 
                  <span className="text-brand-400 font-medium"> auditierbares, skalierbares System</span> – kein Risiko, sondern Asset.
                </p>
              </div>
              <div className="flex-shrink-0">
                <span className="inline-block bg-emerald-500/20 text-emerald-400 px-3 py-1.5 rounded-full text-xs font-bold">
                  EU AI Act Compliant
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Track Record Section */}
      <section className="py-20 md:py-28 bg-slate-800/30">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-10">
            <span className="inline-block bg-amber-500/20 text-amber-300 px-3 py-1.5 rounded-full text-xs font-medium mb-3">
              Track Record
            </span>
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3">
              Bewiesene Ergebnisse
            </h2>
            <p className="text-base text-ink-faint">
              Messbare Value Creation in PE-Portfolios
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {trackRecord.map((item, index) => (
              <div 
                key={index}
                className="bg-surface-raised/50 backdrop-blur border border-hairline rounded-xl p-6 text-center hover:border-teal-500/50 transition-all duration-300"
              >
                <item.icon className="h-8 w-8 text-brand-400 mx-auto mb-3" />
                <div className="mb-2">
                  <span className="text-4xl font-bold text-white">{item.metric}</span>
                  {item.unit && <span className="text-xl text-brand-400 ml-1">{item.unit}</span>}
                </div>
                <p className="text-ink-faint text-sm">{item.label}</p>
              </div>
            ))}
          </div>

          {/* 12-Month Roadmap Highlight */}
          <div className="max-w-[62ch] mt-8">
            <div className="bg-gradient-to-r from-teal-500/10 to-cyan-500/10 border border-teal-500/30 rounded-xl p-5">
              <div className="flex items-center justify-center gap-3 mb-3">
                <Calendar className="h-5 w-5 text-brand-400" />
                <h4 className="text-white font-bold">12-Monats-Roadmap</h4>
              </div>
              <p className="text-ink-muted text-sm text-center">
                Wir liefern Ergebnisse in <span className="text-brand-400 font-semibold">Monaten, nicht Jahren</span>. 
                Klare Meilensteine, messbare KPIs, regelmäßiges Board-Reporting für Ihre Investment-Komitees.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PE-Specific Value Props */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {/* For Operating Partners */}
              <div className="bg-surface-raised/50 border border-hairline rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <BarChart3 className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold">Für Operating Partners</h3>
                    <p className="text-ink-faint text-xs">Operational Excellence</p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {[
                    'Sofortige Quick Wins in 90 Tagen',
                    'Cross-Portfolio Best Practices',
                    'Skalierbare Playbooks für jedes Asset',
                    'Einheitliches KI-Governance Framework'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                      <span className="text-ink-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* For GPs */}
              <div className="bg-surface-raised/50 border border-hairline rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <Scale className="h-5 w-5 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold">Für General Partners</h3>
                    <p className="text-ink-faint text-xs">Risk Mitigation & Exit Value</p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {[
                    'Due Diligence KI-Assessment',
                    'Dokumentierte Compliance (EU AI Act)',
                    'Exit-Story mit Tech-Narrativ',
                    'Nachweisbare Multiple Expansion'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span className="text-ink-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-[62ch] text-center">
            <Award className="h-10 w-10 text-brand-400 mx-auto mb-4" />
            
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-4">
              Bereit für den nächsten Werthebel?
            </h2>

            <p className="text-base text-ink-muted mb-8">
              Diskret. Schnell. Messbar. Lassen Sie uns über die KI-Transformation Ihres Portfolios sprechen.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={() => setIsContactModalOpen(true)}
                className="bg-brand hover:bg-brand-600 text-ink-onBrand px-6 py-3 font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all group"
              >
                Strategisches Gespräch vereinbaren
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Link to="/ki-value-check">
                <Button variant="outline" className="border-hairline-strong text-white hover:bg-surface-raised px-6 py-3">
                  KI-Value-Check für Portfolios
                </Button>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-4 mt-8 pt-8 border-t border-hairline">
              <div className="flex items-center gap-2 text-ink-faint text-xs">
                <Shield className="h-4 w-4" />
                <span>NDA auf Anfrage</span>
              </div>
              <div className="flex items-center gap-2 text-ink-faint text-xs">
                <Lock className="h-4 w-4" />
                <span>100% Vertraulichkeit</span>
              </div>
              <div className="flex items-center gap-2 text-ink-faint text-xs">
                <Clock className="h-4 w-4" />
                <span>Erste Ergebnisse in 90 Tagen</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        pageSource="Private Equity Landing Page"
        buttonContext="PE Case Study Request"
        buttonText="PE-Case Study anfordern"
        prefilledMessage="Ich interessiere mich für die KI-Value-Engine für unser Private Equity Portfolio. Bitte senden Sie mir eine PE-Case Study und vereinbaren Sie ein vertrauliches Gespräch."
      />
    </div>
  );
};

export default PrivateEquity;
